/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {DateTime} from 'luxon';
import PhotoSwipe, {PhotoSwipeOptions, SlideData} from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {renderToString} from 'react-dom/server';
import Skeleton from 'react-loading-skeleton';
import {useSelector} from 'react-redux';
import {getTimeSince} from '~/ViewInvitation/common/TimeSince';
import {useDeletePhoto} from '~/ViewInvitation/hooks/useDeletePhoto';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {
  PhotoDeleteConfirmationModal,
  PhotoDeleteFlagModal,
} from '~/ViewInvitation/routes/Photos/modals';
import {PhotoAdOverlay} from '~/ViewInvitation/routes/Photos/PhotoAdOverlay/PhotoAdOverlay';
import {stateIncremement} from '~/ViewInvitation/routes/Photos/utils';
import {selectEventId, selectIsPremium} from '~/ViewInvitation/selectors/eventDetails';
import {isHostOrCF} from '~/ViewInvitation/utils';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DownloadIcon} from '~library/svg/DownloadIcon';
import {FlagIcon} from '~library/svg/FlagIcon';
import {TrashIcon} from '~library/svg/TrashIcon';
import {ZoomInIcon} from '~library/svg/ZoomInIcon';
import {ZoomOutIcon} from '~library/svg/ZoomOutIcon';

import 'photoswipe/style.css';
import 'photoswipe/dist/photoswipe.css';
import {ghost} from '~libSass/base/_exports.sass';
import 'react-loading-skeleton/dist/skeleton.css';
import './PhotoViewer.sass';

type Photo = SharedPhoto | HostPhoto;

function isSharedPhoto(x: any): x is SharedPhoto {
  return (x as SharedPhoto).created !== undefined;
}
function isHostPhoto(x: any): x is HostPhoto {
  return (x as HostPhoto).photo_id !== undefined;
}

const createdDateTime = (currentImage: Photo) => {
  if (isSharedPhoto(currentImage)) {
    const createdDateTimeParsed = DateTime.fromFormat(currentImage.created, `yyyyMMdd'T'HHmmss`);
    return createdDateTimeParsed.toLocaleString(DateTime.DATE_FULL);
  }
  return undefined;
};

const trashIcon = <TrashIcon className="delete-icon" ratio={0.65} color={ghost} />;
const flagIcon = <FlagIcon className="flag-icon" ratio={0.7} color={ghost} />;
const downloadIcon = renderToString(<DownloadIcon ratio={0.75} color={ghost} />);
const zoomInIcon = <ZoomInIcon ratio={0.65} color={ghost} />;
const zoomOutIcon = renderToString(<ZoomOutIcon ratio={0.65} color={ghost} />);
const closeButton = renderToString(<CloseLineIcon ratio={1.1} color={ghost} />);

interface PhotoViewerProps {
  initialPhotoId: number;
  onClose: () => void;
  isHostModule: boolean;
}

export const PhotoViewer = ({initialPhotoId, onClose, isHostModule = false}: PhotoViewerProps) => {
  const isMediumAndUp = useIsMediumAndUp();
  const {getDisplayUrl, getDownloadUrl} = useImageProxySizer();
  const lightboxRef = useRef<PhotoSwipeLightbox | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0); // TODO: consider modifying this state to hold photo.id as well
  const [showingDeleteMessage, setShowingDeleteMessage] = useState(false);
  const eventId = useSelector(selectEventId);
  const isPremium = useSelector(selectIsPremium);
  const [imageCount, setImageCount] = useState(0);
  const isWebview = window.is_webview;
  const shouldShowAds = !isHostModule && !isPremium;

  const getPhotosQueryArgs = useMemo(
    () => ({
      eventId,
      isHostModule,
      perPage: -1,
      includeCount: true,
    }),
    [eventId, isHostModule]
  );

  const {photos, photoCount, isLoading} = usePhotos(getPhotosQueryArgs);

  const photosRef = useRef(photos);
  photosRef.current = photos;
  const prevPhotoCountRef = useRef(photoCount);

  const onDelete = useCallback(() => {
    const updatedPhotoCount = photos.length;
    const newIndex =
      currentIndex >= updatedPhotoCount ? Math.max(updatedPhotoCount - 1, 0) : currentIndex;
    setCurrentIndex(newIndex);

    // Update PhotoSwipe internal index
    if (lightboxRef.current && lightboxRef.current.pswp) {
      lightboxRef.current.pswp.goTo(newIndex);
      lightboxRef.current.pswp.refreshSlideContent(newIndex);
    }
  }, [photos, currentIndex]);

  useEffect(() => {
    const prevCount = prevPhotoCountRef.current;
    const currentCount = photoCount;

    if (prevCount > currentCount && currentCount > 0) {
      onDelete();
    }

    prevPhotoCountRef.current = currentCount;
  }, [photoCount, onDelete]);

  const {canDeletePhoto, deleteCurrentPhoto, showingFlagMessage, setShowingFlagMessage} =
    useDeletePhoto({
      photos,
      photoCount,
      onClose,
      setShowingDeleteMessage,
      currentIndex,
      isHostModule,
      isPhotoViewer: true,
    });

  const downloadPhoto = (photo?: SlideData) => {
    if (!photo) return;
    const photoUrl = isHostPhoto(photo) ? photo.url : photo.content.url;
    const id = isHostPhoto(photo) ? photo.photo_id : photo.content.photo_id;
    const url = getDownloadUrl(photoUrl);
    const link = document.createElement('a');
    link.href = url;
    link.download = `photo_${id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const createPhotoDetails = (currentImage: SlideData) => (
    <div className="photo-viewer__photo-details">
      {isSharedPhoto(currentImage) ? (
        <Typography
          variant="paragraph1"
          color="ghost"
          aria-label={`Photo taken by ${currentImage.creator.first_name} ${currentImage.creator.last_name}`}
          title={`Photo taken by ${currentImage.creator.first_name} ${currentImage.creator.last_name}`}
        >
          {currentImage.creator.first_name} {currentImage.creator.last_name}
        </Typography>
      ) : (
        <Skeleton width={200} />
      )}
      {isSharedPhoto(currentImage) ? (
        <Typography
          variant="paragraph4"
          color="ghost"
          arial-label={createdDateTime(currentImage)}
          title={createdDateTime(currentImage)}
        >
          {getTimeSince(currentImage.created)}
        </Typography>
      ) : (
        <Skeleton width={110} />
      )}
    </div>
  );

  const showOptions = !isHostModule || isHostOrCF();

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!isLoading && photos.length > 0) {
      const foundIndex = photos.findIndex((photo: Photo) => {
        if (isHostPhoto(photo)) {
          return photo.photo_id === initialPhotoId;
        }
        return photo.id === initialPhotoId;
      });
      const startIndex = foundIndex !== -1 ? foundIndex : 0;
      setCurrentIndex(startIndex);
      const config: PhotoSwipeOptions = {
        bgOpacity: 0.9,
        pswpModule: PhotoSwipe,
        showHideAnimationType: 'fade',
        preload: [3, 4],
        clickToCloseNonZoomable: false,
        preloaderDelay: 0,
      };
      const lightbox = new PhotoSwipeLightbox(config);
      lightbox.on('close', () => {
        setImageCount(stateIncremement);
        onClose();
      });
      lightbox.addFilter('numItems', () => photosRef.current.length);
      lightbox.addFilter('itemData', (_, index) => {
        const photo = photosRef.current[index];
        const photoUrl = isHostModule ? photo.url : photo.content?.url;
        const width = isHostModule ? photo.width : photo.content?.width;
        const height = isHostModule ? photo.height : photo.content?.height;
        return {
          src: getDisplayUrl(photoUrl),
          alt: photo.comment || photo.post_type,
          width,
          height,
          ...photo,
        };
      });
      lightbox.on('change', () => {
        setCurrentIndex(lightbox?.pswp?.currIndex || 0);
        if (shouldShowAds) {
          setImageCount(stateIncremement);
        }
        if (showOptions) {
          const canDelete = isHostModule || canDeletePhoto(lightbox?.pswp?.currSlide?.data);
          (document.querySelector('.delete-icon')! as HTMLElement).style.display = canDelete
            ? 'inline'
            : 'none';
          (document.querySelector('.flag-icon')! as HTMLElement).style.display = canDelete
            ? 'none'
            : 'inline';
        }
      });
      lightbox.addFilter('uiElement', (element, data) => {
        if ((isWebview || !isMediumAndUp) && data.name === 'zoom') {
          element.style.display = 'none';
        }
        return element;
      });
      lightbox.on('uiRegister', () => {
        lightbox?.pswp?.ui?.registerElement({
          name: 'caption',
          order: 9,
          isButton: false,
          appendTo: 'root',
          html: '',
          onInit: (el) => {
            lightbox?.pswp?.on('change', () => {
              const currSlideElement = lightbox?.pswp?.currSlide?.data;
              if (currSlideElement && !isHostModule) {
                el.innerHTML = renderToString(createPhotoDetails(currSlideElement));
              }
            });
          },
        });
        lightbox?.pswp?.ui?.registerElement({
          name: 'delete-button',
          ariaLabel: 'Delete',
          order: 8,
          isButton: true,
          html: showOptions
            ? renderToString(
                <span>
                  {trashIcon}
                  {flagIcon}
                </span>
              )
            : undefined,
          onClick: () => {
            const canDelete = canDeletePhoto(lightbox?.pswp?.currSlide?.data);
            if (canDelete) {
              const pswpElement = document.querySelector('.pswp') as HTMLElement;
              if (pswpElement) {
                pswpElement.setAttribute('inert', '');
                pswpElement.style.pointerEvents = 'none';
              }
              setShowingDeleteMessage(true);
            } else {
              setShowingFlagMessage(true);
            }
          },
        });
        if (!isWebview) {
          lightbox?.pswp?.ui?.registerElement({
            name: 'download-button',
            ariaLabel: 'Download',
            order: 9,
            isButton: true,
            html: downloadIcon,
            onClick: () => {
              downloadPhoto(lightbox?.pswp?.currSlide?.data);
            },
          });
        }
      });
      lightbox.on('zoomPanUpdate', (data) => {
        const canZoomIn = data.slide.currZoomLevel < 1;
        document.getElementById('zoom-icon')!.innerHTML = canZoomIn
          ? renderToString(zoomInIcon)
          : zoomOutIcon;
      });
      lightbox.addFilter('uiElement', (element, data) => {
        if (data.name === 'close') {
          element.innerHTML = closeButton;
        }
        if (data.name === 'zoom') {
          element.innerHTML = renderToString(<span id="zoom-icon">{zoomInIcon}</span>);
        }
        return element;
      });
      lightbox.init();
      lightbox.loadAndOpen(startIndex);
      lightboxRef.current = lightbox;
      return () => {
        lightbox.destroy();
        lightboxRef.current = null;
      };
    }
    return undefined;
  }, [isLoading, initialPhotoId]);

  const enablePswpElement = useCallback(() => {
    const pswpElement = document.querySelector('.pswp') as HTMLElement;
    if (pswpElement) {
      pswpElement.removeAttribute('inert');
      pswpElement.style.pointerEvents = '';
    }
    lightboxRef.current?.pswp?.refreshSlideContent?.(
      lightboxRef.current?.pswp?.currSlide?.index || currentIndex
    );
  }, [currentIndex]);

  const handleApproveDeleteModal = useCallback(async () => {
    await deleteCurrentPhoto();
    enablePswpElement();
  }, [deleteCurrentPhoto, enablePswpElement]);

  const handleCloseDeleteModal = useCallback(() => {
    enablePswpElement();
    setShowingDeleteMessage(false);
  }, [enablePswpElement]);

  return createPortal(
    <div className="photo-viewer">
      {showingDeleteMessage && (
        <PhotoDeleteConfirmationModal
          onApprove={handleApproveDeleteModal}
          onClose={handleCloseDeleteModal}
          isHostModule={isHostModule}
          isPhotoViewer
        />
      )}
      {showingFlagMessage && <PhotoDeleteFlagModal onClose={() => setShowingFlagMessage(false)} />}
      <PhotoAdOverlay imageCount={imageCount} />
    </div>,
    document.body
  );
};
